import z from 'zod';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
const today = new Date();
today.setHours(0, 0, 0, 0);

const MAX_18_DIGIT = 10 ** 18 - 1;

export const schema = z.object({
  mobilenumber: z.string().refine(
    (value) => {
      const phone = parsePhoneNumberFromString(value);
      return phone?.isValid();
    },
    {
      message: 'Enter a valid mobile number',
    }
  ),
});

export const loginSchema = z.object({
  username: z.string().trim().min(3, 'Username must be at least 3 characters long'),
  password: z.string().min(1, 'Please enter a password'),
  // captcha: z.string().trim().min(1, { message: 'Captcha is required' }),
});

export const bankerSchema = z.object({
  firstname: z.preprocess(
    (val) => (typeof val === 'string' ? val.trim() : val),
    z
      .string()
      .refine((val) => val.length >= 3, {
        message: 'First name must be at least 3 characters',
      })
      .refine((val) => /^[A-Za-zÀ-ÖØ-öø-ÿ\s'-]+$/.test(val), {
        message: 'First name can only contain letters, spaces, apostrophes, or hyphens',
      })
  ),

  lastname: z.preprocess(
    (val) => (typeof val === 'string' ? val.trim() : val),
    z
      .string()
      .refine((val) => val.length >= 3, {
        message: 'Last name must be at least 3 characters',
      })
      .refine((val) => /^[A-Za-zÀ-ÖØ-öø-ÿ\s'-]+$/.test(val), {
        message: 'Last name can only contain letters, spaces, apostrophes, or hyphens',
      })
  ),

  username: z.preprocess(
    (val) => (typeof val === 'string' ? val.trim() : val),
    z
      .string()
      .refine((val) => val.length >= 3, {
        message: 'Username must be at least 3 characters',
      })
      .refine((val) => /^[a-z][a-z0-9_-]*$/.test(val), {
        message:
          'Username must start with a lowercase letter and can contain lowercase letters, numbers, hyphens, and underscores',
      })
  ),

  mobilenumber: z.string().refine(
    (value) => {
      const phone = parsePhoneNumberFromString(value);
      return phone?.isValid();
    },
    {
      message: 'Invalid mobile number',
    }
  ),

  password: z.preprocess(
    (val) => (typeof val === 'string' ? val.trim() : val),
    z
      .string()
      .min(8, 'Password must be at least 8 characters long')
      .max(20, 'Password cannot exceed 20 characters')
  ),

  transactionCode: z.preprocess(
    (val) => (typeof val === 'string' ? val.trim() : val),
    z.string().regex(/^\d{5}$/, 'Transaction code must be exactly 5 digits')
  ),
});

export const AddBankSchema = z
  .object({
    accountNumber: z
      .string()
      .nonempty('Account number is required')
      .regex(/^[0-9]+$/, 'Account number must be numeric')
      .min(5, 'Account number must be at least 5 digits')
      .max(20, 'Account number cannot exceed 20 digits'),

    ifsc: z
      .string()
      .nonempty('IFSC code is required')
      .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Invalid IFSC code'),

    accountHolder: z
      .string()
      .nonempty('Account holder name is required')
      .min(3, 'Account holder name must be at least 3 characters'),

    accountType: z.string().nonempty('Account type is required'),

    bankName: z
      .string()
      .nonempty('Bank name is required')
      .min(3, 'Bank name must be at least 3 characters'),

    selectType: z.string().nonempty('Select type is required'),

    minAmount: z.preprocess(
      (val) => Number(val),
      z
        .number()
        .positive('Minimum amount must be greater than 0')
        .max(MAX_18_DIGIT, 'Minimum amount must be at most 18 digits')
    ),

    maxAmount: z.preprocess(
      (val) => Number(val),
      z
        .number()
        .positive('Maximum amount must be greater than 0')
        .max(MAX_18_DIGIT, 'Maximum amount must be at most 18 digits')
    ),
  })
  .refine((data) => data.maxAmount > data.minAmount, {
    message: 'Maximum amount must be greater than or equal to minimum amount',
    path: ['maxAmount'],
  });

export const passwordSchema = z
  .object({
    oldPassword: z.string().min(1, 'Current password is required'),
    newPassword: z
      .string()
      .min(8, 'Password must be at least 8 characters long')
      .max(20, 'Password must not exceed 20 characters'),
    confirmPassword: z.string().min(1, 'Confirm password is required'),
  })
  // New password should not match old password
  .refine((data) => data.oldPassword !== data.newPassword, {
    message: 'New password must be different from current password',
    path: ['newPassword'],
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

export const walletSchema = z
  .object({
    mobilenumber: z.string().refine(
      (value) => {
        const phone = parsePhoneNumberFromString(value);
        return phone?.isValid();
      },
      {
        message: 'Enter a valid mobile number',
      }
    ),
    name: z
      .string()
      .trim()
      .min(3, 'Name must be at least 3 characters')
      .max(20, 'Name must be maximum 20 characters'),
    minAmount: z.preprocess(
      (val) => Number(val),
      z
        .number({
          invalid_type_error: 'Minimum amount must be a number',
        })
        .min(0, 'Minimum must be ≥ 0')
    ),

    maxAmount: z.preprocess(
      (val) => Number(val),
      z
        .number({
          invalid_type_error: 'Maximum amount must be a number',
        })
        .min(0, 'Maximum must be ≥ 0')
    ),
  })
  .superRefine((data, ctx) => {
    if (data.maxAmount <= data.minAmount) {
      ctx.addIssue({
        path: ['maxAmount'],
        message: 'Maximum amount must be greater than minimum amount',
        code: z.ZodIssueCode.custom,
      });
    }
  });

export const upiQrSchema = z
  .object({
    upiId: z
      .string()
      .trim()
      .nonempty('UPI ID is required')
      .regex(/^[\w.-]{2,256}@[a-zA-Z]{2,64}$/, 'Enter a valid UPI ID'),
    type: z.string().nonempty('Type is required'),
    qrCode: z.string().nonempty('QR Image is required'),
    minAmount: z.preprocess(
      (val) => Number(val),
      z
        .number({
          invalid_type_error: 'Minimum amount must be a number',
        })
        .gt(0, 'Minimum amount must be greater than 0')
        .max(MAX_18_DIGIT, 'Minimum amount must be at most 18 digits')
    ),
    maxAmount: z.preprocess(
      (val) => Number(val),
      z
        .number({
          invalid_type_error: 'Maximum amount must be a number',
        })
        .min(0, 'Maximum must be ≥ 0')
        .max(MAX_18_DIGIT, 'Maximum amount must be at most 18 digits')
    ),
  })
  .superRefine((data, ctx) => {
    if (data.maxAmount <= data.minAmount) {
      ctx.addIssue({
        path: ['maxAmount'],
        message: 'Maximum amount must be greater than minimum amount',
        code: z.ZodIssueCode.custom,
      });
    }
  });

// const ethOrBscRegex = /^0x[a-fA-F0-9]{40}$/;
// const tronRegex = /^T[a-zA-Z0-9]{33}$/;
// const omniRegex = /^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/;

export const cryptoSchema = z
  .object({
    walletAddress: z
      .string()
      .nonempty('Wallet address is required')
      .min(3, 'Wallet address must be at least 3 characters')
      .max(50, 'Wallet address must not exceed 50 characters')
      .refine((val) => val.trim().length > 0, {
        message: 'Wallet address cannot be empty or spaces only',
      }),
    networkId: z.number().min(1, 'Please select a network'),
    qrImage: z
      .string()
      .nullable()
      .refine((val) => val !== null && val.length > 0, {
        message: 'QR Image is required',
      }),
    minAmount: z.preprocess(
      (val) => Number(val),
      z
        .number({
          invalid_type_error: 'Minimum amount must be a number',
        })
        .gt(0, 'Minimum amount must be greater than 0')
        .max(MAX_18_DIGIT, 'Minimum amount must be at most 18 digits')
    ),

    maxAmount: z.preprocess(
      (val) => Number(val),
      z
        .number({
          invalid_type_error: 'Maximum amount must be a number',
        })
        .min(0, 'Maximum must be ≥ 0')
        .max(MAX_18_DIGIT, 'Maximum amount must be at most 18 digits')
    ),
  })
  .superRefine((data, ctx) => {
    if (data.maxAmount <= data.minAmount) {
      ctx.addIssue({
        path: ['maxAmount'],
        message: 'Maximum amount must be greater than minimum amount',
        code: z.ZodIssueCode.custom,
      });
    }
  });
export const addNotificationSchema = z.object({
  type: z.preprocess(
    (val) => (typeof val === 'string' ? val.trim() : val),
    z
      .string()
      .min(2, 'Type is required')
      .regex(/^[A-Za-z\s'-]+$/, 'Type cannot contain numbers')
  ),
  title: z.preprocess(
    (val) => (typeof val === 'string' ? val.trim() : val),
    z.string().min(2, 'Title is required')
  ),
  body: z.preprocess(
    (val) => (typeof val === 'string' ? val.trim() : val),
    z.string().min(2, 'Message is required')
  ),
  data: z.record(z.any()).optional(),
});
export const addbannerSchema = z.object({
  type: z.string().min(1, 'Banner type is required'),
  name: z.string().min(1, 'Banner name is required'),
  // heading: z.string().min(1, 'Heading is required'),
  // description: z.string().min(1, 'Description is required'),
  platform: z.string().min(1, 'Platform type is required'),
  size: z.string().min(1, 'Size is required'),
  bannerImage: z.string().min(1, 'Banner image is required'),
});

export const manageRoleSchema = (type?: string) =>
  z.object({
    firstname: z.preprocess(
      (val) => (typeof val === 'string' ? val.trim() : val),
      z
        .string()
        .min(4, 'First name must be 4 to 16 characters.')
        .max(16, 'First name must be 4 to 16 characters.')
    ),

    // username: z.preprocess(
    //   (val) => (typeof val === 'string' ? val.trim() : val),
    //   z
    //     .string()
    //     .min(4, 'Username must be 4 to 16 characters.')
    //     .max(16, 'Username must be 4 to 16 characters.')
    // ),

    email: z.string().email('Enter a valid email address'),

    // Password only required in CREATE
    password:
      type === 'edit' || type === 'view'
        ? z.string().optional()
        : z
            .string()
            .min(8, 'Password must be at least 8 characters long')
            .max(20, 'Password cannot exceed 20 characters'),

    mobile: z.string(),

    // Transaction Code only required in CREATE
    // transactionCode:
    //   type === 'edit' || type === 'view'
    //     ? z.string().optional()
    //     : z
    //         .string()
    //         .length(5, 'Transaction password must be exactly 5 digits')
    //         .regex(/^\d{5}$/, 'Transaction password must contain only numbers'),

    role: z.number().min(1, 'Role is required'),
  });

export const addMemberSchema = ({
  minPartnership = 0,
  isOwner = false,
  createUser = false,
}: {
  minPartnership?: number;
  isOwner?: boolean;
  createUser?: boolean;
}) =>
  z.object({
    username: z.preprocess(
      (val) => (typeof val === 'string' ? val.trim() : val),
      z
        .string({
          required_error: 'Username is required',
        })
        .min(3, 'Username must be at least 3 characters')
        .max(20, 'Username cannot exceed 20 characters')
    ),

    mobile: z.string().optional(),

    password: z.preprocess(
      (val) => (typeof val === 'string' ? val.trim() : val),
      z
        .string()
        .min(8, 'Password must be at least 8 characters long')
        .max(20, 'Password cannot exceed 20 characters')
    ),
    // transactionCode: z
    //   .string()
    //   .length(5, 'Transaction password must be exactly 5 digits')
    //   .regex(/^\d{5}$/, 'Transaction password must contain only numbers'),
    partnership: z.preprocess(
      (val) => (val === '' ? undefined : Number(val)),
      z
        .number({
          required_error: 'Partnership is required',
          invalid_type_error: 'Partnership must be a number',
        })
        .int('Partnership must be a whole number')
        .refine(
          (value) => {
            // ⭐ Case 1: Any role creating USER → must be 100
            if (createUser) {
              return value === 100;
            }

            // ⭐ Case 2: Owner creating Subadmin → 1–99
            if (isOwner) {
              return value > 0 && value < 100;
            }

            // ⭐ Case 3: Non-owner creating Member → min → 99
            return value > minPartnership && value <= 99;
          },
          {
            message: getPartnershipErrorMessage(isOwner, createUser, minPartnership),
          }
        )
    ),
    creditLimit: z
      .number({ invalid_type_error: 'Deposit Amount must be a number' })
      .positive('Deposit Amount must be positive')
      .min(1, 'Min Deposit amount must be greater than 0')
      .max(999999999, 'Deposit Amount cannot exceed 9 digits')
      .optional(),
  });
const getPartnershipErrorMessage = (
  isOwner: boolean,
  createUser: boolean,
  minPartnership: number
) => {
  if (isOwner && createUser) {
    return 'User must have 100% partnership';
  }

  if (isOwner && !createUser) {
    return 'Subadmin partnership must be between 1% and 99%';
  }

  return `Partnership must be between ${minPartnership + 1}% and 99%`;
};

export const supportSchema = z.object({
  type: z.string().min(1, 'Type is required'),
  label: z.string().min(1, 'Label is required'),
  number: z.string().min(1, 'number/link is required'),
});

export const changePasswordSchema = z
  .object({
    newPassword: z.preprocess(
      (val) => (typeof val === 'string' ? val.trim() : val),
      z
        .string()
        .min(8, 'Password must be at least 8 characters long')
        .max(20, 'Password cannot exceed 20 characters')
    ),
    confirmPassword: z.string().min(8, 'Confirm Password is required'),
    // transactionCode: z
    //   .string()
    //   .length(5, 'Transaction password must be exactly 5 digits')
    //   .regex(/^\d{5}$/, 'Transaction password must contain only numbers'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

export const UpdateGameSchema = z.object({
  gameCode: z
    .string()
    .trim()
    .regex(/^\S+$/, 'Spaces are not allowed')
    .min(3, 'Game Code is Required'),

  gameName: z.string().trim().min(3, 'Game Name Required'),

  priority: z.preprocess(
    (val) => {
      if (val === '' || val === null || val === undefined) return undefined;
      return Number(val);
    },
    z
      .number({
        required_error: 'Priority is required',
        invalid_type_error: 'Priority must be a number',
      })
      .int('Priority must be an integer')
      .nonnegative('Priority must be 0 or greater')
      .max(9999, 'Priority can be max 4 digits')
  ),
  category: z.string().trim().min(1, 'Category is Required'),
  provider: z.string().trim().min(1, 'Provider is Required'),
  trendingStatus: z.boolean().optional(),
  thumbnail: z.any().optional(),
});

export const AmountSchema = z.object({
  amount: z
    .number({ invalid_type_error: 'Amount must be a number' })
    .positive('Amount must be positive')
    .min(1, 'Min amount must be greater than 0')
    .max(999999999, 'Amount cannot exceed 9 digits'),
  currentBalance: z.string().optional(),
  updatedBalance: z.string().optional(),
  // transactionCode: z
  //   .string()
  //   .length(5, 'Transaction password must be exactly 5 digits')
  //   .regex(/^\d{5}$/, 'Transaction password must contain only numbers'),
});

export const CoinTransactionSchema = z.object({
  amount: z.coerce
    .number({ invalid_type_error: 'Coin must be a number' })
    .positive('Coin must be positive')
    .min(1, 'Min coin must be greater than 0')
    .max(999999999, 'Coin cannot exceed 9 digits'),
});

export const getMinMaxSchema = (currentBalance: number) =>
  z
    .object({
      minAmount: z.coerce
        .number({
          required_error: 'Min amount is required',
          invalid_type_error: 'Enter a valid number',
        })
        .min(1, 'Min amount must be greater than 0')
        .refine((val) => val <= currentBalance, {
          message: `Min amount cannot be greater than current balance (${currentBalance})`,
        }),

      maxAmount: z.coerce
        .number({
          required_error: 'Max amount is required',
          invalid_type_error: 'Enter a valid number',
        })
        .min(1, 'Max amount must be greater than 0')
        .refine((val) => val <= currentBalance, {
          message: `Max amount cannot be greater than current balance (${currentBalance})`,
        }),
    })
    .refine((data) => data.maxAmount >= data.minAmount, {
      message: 'Max amount must be greater than or equal to Min amount',
      path: ['maxAmount'],
    });

export const creditLimitSchema = z.object({
  creditLimit: z
    .number({
      required_error: 'Credit Limit is required',
      invalid_type_error: 'Credit Limit must be a number',
    })
    .min(1, 'Credit Limit must be greater than 0')
    .max(99999999, 'Maximum allowed credit limit is 9999999'),

  // transactionCode: z
  //   .string()
  //   .length(5, 'Transaction password must be exactly 5 digits')
  //   .regex(/^\d{5}$/, 'Transaction password must contain only numbers'),
});

const TEN_DIGIT_MAX = 9_999_999_999;

export const betSchema = z.object({
  inPlayMinBetAmount: z.coerce.number().nonnegative('Required').max(TEN_DIGIT_MAX, 'Max 10 digits'),

  inPlayMaxBetAmount: z.coerce.number().nonnegative('Required').max(TEN_DIGIT_MAX, 'Max 10 digits'),

  offPlayMaxBetAmount: z.coerce
    .number()
    .nonnegative('Required')
    .max(TEN_DIGIT_MAX, 'Max 10 digits'),

  offPlayMinBetAmount: z.coerce
    .number()
    .nonnegative('Required')
    .max(TEN_DIGIT_MAX, 'Max 10 digits'),

  potentialProfit: z.coerce.number().nonnegative('Required').max(TEN_DIGIT_MAX, 'Max 10 digits'),

  minRate: z.coerce.number().nonnegative('Required').max(TEN_DIGIT_MAX, 'Max 10 digits'),

  maxRate: z.coerce.number().nonnegative('Required').max(TEN_DIGIT_MAX, 'Max 10 digits'),
});

export const baseBonusSchema = z.object({
  bonusName: z
    .string()
    .min(1, 'Bonus name is required')
    .max(100, 'Bonus name must be under 100 characters'),

  description: z
    .string()
    .min(1, 'Description is required')
    .max(500, 'Description must be under 500 characters'),

  dateFrom: z.string().min(1, 'Start date is required'),

  dateTo: z.string().min(1, 'End date is required'),

  isEnabled: z.boolean(),
});

export const joiningBonusSchema = baseBonusSchema
  .extend({
    isEnabled: z.boolean(),

    bonusPercentage: z
      .number({
        required_error: 'Bonus percentage is required',
        invalid_type_error: 'Bonus percentage must be a number',
      })
      .min(1, 'Bonus percentage must be at least 1%')
      .max(100, 'Bonus percentage cannot exceed 100%'),

    turnoverMultiplier: z
      .number({
        required_error: 'Turnover multiplier is required',
        invalid_type_error: 'Turnover multiplier must be a number',
      })
      .min(0, 'Turnover multiplier must be at least 0')
      .max(100, 'Turnover multiplier cannot exceed 100'),

    minDepositAmount: z
      .number({
        required_error: 'Minimum deposit amount is required',
        invalid_type_error: 'Minimum deposit must be a number',
      })
      .min(1, 'Minimum deposit must be greater than 0'),

    maxBonusAmount: z
      .number({
        required_error: 'Maximum bonus amount is required',
        invalid_type_error: 'Maximum bonus must be a number',
      })
      .min(1, 'Maximum bonus must be greater than 1'),
  })
  .refine((data) => new Date(data.dateFrom) <= new Date(data.dateTo), {
    message: 'End date must be after start date',
    path: ['dateTo'],
  });
export const reFerrallBonusSchema = baseBonusSchema
  .extend({
    isEnabled: z.boolean(),

    bonusPercentage: z
      .number()
      .min(1, 'Bonus percentage must be at least 1%')
      .max(100, 'Bonus percentage cannot exceed 100'),

    minDepositAmount: z.number().min(1, 'Minimum deposit must be 1 or greater'),
    releaseCondition: z.enum(['TURNOVER', 'INSTANT_WITHDRAWAL'], {
      required_error: 'Release condition is required',
    }),
    maxBonusAmount: z.number().min(1, 'Maximum bonus must be 1 or greater'),

    referrerMinPayoutAmount: z.number().min(1, 'Referrer minimum payout must be 1 or greater'),

    referrerMaxBonusAmount: z.number().min(1, 'Referrer max bonus must be 1 or greater'),

    referrerPercentage: z
      .number()
      .min(1, 'Referrer percentage must be at least 1%')
      .max(100, 'Referrer percentage cannot exceed 100'),

    turnoverMultiplier: z
      .number()
      .min(0, 'Turnover multiplier must be 0 or greater')
      .max(100, 'Turnover multiplier is too high')
      .optional(),
  })

  .refine((data) => new Date(data.dateFrom) <= new Date(data.dateTo), {
    message: 'End date must be after start date',
    path: ['dateTo'],
  });
// .refine((data) => data.maxBonusAmount >= data.minDepositAmount, {
//   message: 'Max bonus must be greater than or equal to min deposit',
//   path: ['maxBonusAmount'],
// })
// .refine((data) => data.referrerMaxBonusAmount >= data.referrerMinPayoutAmount, {
//   message: 'Referrer max bonus must be ≥ min payout',
//   path: ['referrerMaxBonusAmount'],
// });

export const referralLossComissionSchema = baseBonusSchema
  .extend({
    isEnabled: z.boolean(),

    bonusPercentage: z
      .number()
      .min(1, 'Bonus percentage must be at least 1%')
      .max(100, 'Bonus percentage cannot exceed 100'),

    referrerMinPayoutAmount: z.number().min(1, 'Referrer minimum payout must be 1 or greater'),

    referrerMaxBonusAmount: z.number().min(0, 'Referrer maximum bonus must be 0 or greater'),

    releaseCondition: z.enum(['Daily', 'Weekly', 'Monthly'], {
      required_error: 'Release condition is required',
    }),
  })
  // 🔽 Cross-field validations
  .refine((data) => new Date(data.dateFrom) <= new Date(data.dateTo), {
    message: 'End date must be after start date',
    path: ['dateTo'],
  });

// .refine((data) => data.referrerMaxBonusAmount >= data.referrerMinPayoutAmount, {
//   message: 'Referrer max bonus must be greater than or equal to min payout',
//   path: ['referrerMaxBonusAmount'],
// });

export const lossBackBonusSchema = baseBonusSchema
  .extend({
    isEnabled: z.boolean(),

    bonusPercentage: z
      .number()
      .min(1, 'Bonus percentage must be at least 1%')
      .max(100, 'Bonus percentage cannot exceed 100'),

    maxBonusAmount: z.number().min(1, 'Max bonus must be 1 or greater'),

    releaseCondition: z.enum(['Daily', 'Weekly', 'Monthly'], {
      required_error: 'Release condition is required',
    }),

    lossThreshold: z.number().min(100, 'Loss threshold must be 100 or greater'),
  })

  // 🔽 Cross-field validation
  .refine((data) => new Date(data.dateFrom) <= new Date(data.dateTo), {
    message: 'End date must be after start date',
    path: ['dateTo'],
  });
const tierSchema = z
  .object({
    minDeposit: z.number().min(0),
    maxDeposit: z.number().min(0),
    bonusPercentage: z.number().min(0).max(100),
  })
  .refine((t) => t.minDeposit < t.maxDeposit, {
    path: ['maxDeposit'],
    message: 'Max deposit must be greater than min deposit',
  });

const singleTierSchema = baseBonusSchema.extend({
  enableMultiTierBonus: z.literal(false),
  turnoverMultiplier: z.number().optional(),
  bonusPercentage: z.number().min(1, 'Max percentage must be 1 or greater'),
  minDepositAmount: z.number().min(1, 'Max deposit must be 1 or greater'),
  maxBonusAmount: z.number().min(1, 'Max bonus must be 1 or greater'),

  refillTiers: z.undefined().optional(),
});
const refillTiersSchema = z
  .array(tierSchema)
  .min(1, 'At least one tier is required')
  .superRefine((tiers, ctx) => {
    for (let i = 1; i < tiers.length; i++) {
      const prev = tiers[i - 1];
      const curr = tiers[i];

      if (!prev || !curr) continue;

      if (curr.minDeposit <= prev.maxDeposit) {
        ctx.addIssue({
          path: [i, 'minDeposit'],
          message: `Tier ${i + 1} min deposit must be greater than ${prev.maxDeposit}`,
          code: z.ZodIssueCode.custom,
        });
      }
    }
  });

const multiTierSchema = baseBonusSchema.extend({
  enableMultiTierBonus: z.literal(true),

  bonusPercentage: z.undefined().optional(),
  minDepositAmount: z.undefined().optional(),
  maxBonusAmount: z.undefined().optional(),
  turnoverMultiplier: z.number().optional(),
  refillTiers: refillTiersSchema,
});

export const refilSchema = z.discriminatedUnion('enableMultiTierBonus', [
  singleTierSchema,
  multiTierSchema,
]);
export type RefillFormValues = z.infer<typeof refilSchema>;

export const createRoleSchema = () =>
  z.object({
    enableAll: z.boolean().optional(),
    name: z.preprocess(
      (val) => (typeof val === 'string' ? val.trim() : val),
      z
        .string()
        .min(4, 'Name must be 4 to 16 characters.')
        .max(100, 'Name must be 4 to 100 characters.')
    ),

    description: z.preprocess(
      (val) => (typeof val === 'string' ? val.trim() : val),
      z.string().min(4, 'Description must be at least 4 characters.')
    ),

    privilegesWithAccess: z.array(
      z.object({
        privilegeId: z.number(),
        hasAccess: z.boolean(),
        access: z.record(z.string(), z.boolean()),
      })
    ),
  });
